// GraficiPaniera.jsx
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import {
  AreaChart, Area,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';

// ─── PALETTE — ben contrastata, visibile su sfondo chiaro e scuro ──────────────
const PALETTE = [
  '#6366f1', // indigo
  '#f59e0b', // amber
  '#10b981', // emerald
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
];

const lineaColor = (linea, allLinee) => {
  if (!linea) return '#94a3b8';
  const idx = allLinee.indexOf(linea);
  return PALETTE[idx % PALETTE.length];
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const getMese = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const getAnno = (iso) => {
  if (!iso) return null;
  return new Date(iso).getFullYear();
};

const MESI_IT = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
const meseBreve = (mmyyyy) => {
  if (!mmyyyy) return '';
  const [mm, yyyy] = mmyyyy.split('/');
  return `${MESI_IT[parseInt(mm,10)-1]} '${yyyy.slice(2)}`;
};

const fmt = (n) => Number(n).toLocaleString('it-IT');

// ─── TOOLTIP ─────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const sorted = [...payload].sort((a,b) => (b.value||0)-(a.value||0));
  const total  = sorted.reduce((s,p) => s + (p.value||0), 0);
  return (
    <div className="bg-card border border-border/60 rounded-2xl shadow-2xl px-4 py-3.5 min-w-[170px] pointer-events-none">
      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50 mb-2.5 pb-2 border-b border-border/20">
        {label}
      </p>
      {sorted.map(p => (
        <div key={p.dataKey ?? p.name} className="flex items-center justify-between gap-4 mb-1.5">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.color ?? p.fill }} />
            <span className="text-[10px] font-bold text-muted-foreground/70">
              {p.name === p.dataKey ? `${p.name}` : p.name}
            </span>
          </div>
          <span className="text-[12px] font-black text-foreground tabular-nums">{fmt(p.value)}</span>
        </div>
      ))}
      {sorted.length > 1 && (
        <div className="flex justify-between pt-2 mt-1 border-t border-border/20">
          <span className="text-[9px] font-black uppercase text-muted-foreground/40">Totale</span>
          <span className="text-[12px] font-black text-primary tabular-nums">{fmt(total)}</span>
        </div>
      )}
    </div>
  );
};

// ─── KPI CARD ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, icon, color = '#6366f1', trend }) => (
  <div className="relative flex flex-col gap-3 p-5 rounded-3xl bg-card border border-border/40 overflow-hidden group hover:border-border/60 transition-colors">
    <div className="absolute inset-0 rounded-3xl transition-opacity opacity-0 group-hover:opacity-100"
      style={{ background: `radial-gradient(circle at 80% 20%, ${color}10, transparent 60%)` }} />
    <div className="flex items-center justify-between relative z-10">
      <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">{label}</span>
      <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
        <Icon name={icon} size={14} style={{ color }} />
      </div>
    </div>
    <div className="flex items-end justify-between relative z-10 gap-2">
      <span className="text-3xl font-black tracking-tighter leading-none" style={{ color }}>{value ?? '—'}</span>
      {trend != null && (
        <span className={`text-[10px] font-black flex items-center gap-0.5 mb-0.5 ${trend >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
          <Icon name={trend >= 0 ? 'TrendingUp' : 'TrendingDown'} size={11} />
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    {sub && <span className="text-[9px] font-bold text-muted-foreground/30 uppercase tracking-widest relative z-10 leading-tight">{sub}</span>}
  </div>
);

// ─── CHART CARD ──────────────────────────────────────────────────────────────
const ChartCard = ({ title, subtitle, contextLabel, icon, children, actions }) => (
  <div className="flex flex-col gap-5 p-6 rounded-3xl bg-muted/[0.06] border border-border/30 hover:border-border/50 transition-colors">
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="flex items-start gap-3">
        {icon && (
          <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center shrink-0 mt-0.5">
            <Icon name={icon} size={13} className="text-primary/70" />
          </div>
        )}
        <div className="flex flex-col gap-0.5">
          <p className="text-[12px] font-black uppercase tracking-widest text-foreground/80">{title}</p>
          {subtitle && <p className="text-[9px] font-bold text-muted-foreground/40 uppercase tracking-wider">{subtitle}</p>}
          {/* Context label — mostra il filtro attivo dentro il grafico */}
          {contextLabel && (
            <div className="flex items-center gap-1.5 mt-1">
              <div className="w-1 h-1 rounded-full bg-primary/50" />
              <span className="text-[9px] font-black text-primary/60 uppercase tracking-widest">{contextLabel}</span>
            </div>
          )}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
    {children}
  </div>
);

// ─── TOGGLE ──────────────────────────────────────────────────────────────────
const ChartToggle = ({ value, onChange, options }) => (
  <div className="flex gap-0.5 bg-muted/50 border border-border/30 p-0.5 rounded-xl">
    {options.map(o => (
      <button
        key={o.value}
        onClick={() => onChange(o.value)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all duration-150 ${
          value === o.value
            ? 'bg-card shadow text-foreground border border-border/40'
            : 'text-muted-foreground/40 hover:text-muted-foreground'
        }`}
      >
        <Icon name={o.icon} size={10} />
        {o.label}
      </button>
    ))}
  </div>
);

// ─── FILTER PILL ──────────────────────────────────────────────────────────────
const FilterGroup = ({ label, options, value, onChange, color }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/30 shrink-0 min-w-[32px]">{label}</span>
    <div className="flex gap-1.5 flex-wrap">
      {options.map(o => {
        const active = value === o;
        return (
          <button
            key={o}
            onClick={() => onChange(o)}
            className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-150 border ${
              active
                ? 'text-white border-transparent shadow-sm'
                : 'bg-muted/20 border-border/25 text-muted-foreground/50 hover:text-foreground hover:border-border/50'
            }`}
            style={active ? { background: color || '#6366f1' } : {}}
          >
            {o === 'TUTTO' ? 'Tutti' : o}
          </button>
        );
      })}
    </div>
  </div>
);

// ─── LEGENDA PILLS ────────────────────────────────────────────────────────────
const LegendaPills = ({ linee, allLinee, dati }) => (
  <div className="flex flex-wrap gap-2 pt-1 border-t border-border/10">
    {linee.map(l => {
      const tot = dati ? dati.reduce((s,d) => s + (d[l]||0), 0) : null;
      const col = lineaColor(l, allLinee);
      return (
        <div key={l} className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl border"
          style={{ background: `${col}12`, borderColor: `${col}30` }}>
          <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: col }} />
          <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: col }}>
             {l}{tot !== null ? ` · ${tot}` : ''}
          </span>
        </div>
      );
    })}
  </div>
);

// ─── COMPONENTE PRINCIPALE ────────────────────────────────────────────────────
const GraficiPaniera = ({ isOpen, onClose }) => {
  const [rows, setRows]           = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [annoSel, setAnnoSel]     = useState('TUTTO');
  const [colataSel, setColataSel] = useState('TUTTO');
  const [carroSel, setCarroSel]   = useState('TUTTO');
  const [lineaSel, setLineaSel]   = useState('TUTTO');
  const [anni, setAnni]           = useState([]);
  const [colate, setColate]       = useState([]);
  const [carri, setCarri]         = useState([]);
  const [lineeAll, setLineeAll]   = useState([]);

  const [tipoTrend, setTipoTrend] = useState('area'); // area | bar | line
  const [tipoLinea, setTipoLinea] = useState('bar');  // bar | pie
  const [tipoAnno, setTipoAnno]   = useState('bar');  // bar | line

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('cilindripaniera')
        .select('ncilindro, linea, carropaniera, colata, dataintervento, tipointervento')
        .ilike('tipointervento', 'SMONTATO')
        .not('dataintervento', 'is', null)
        .not('linea', 'is', null)
        .order('dataintervento', { ascending: true });
      if (error) throw error;

      const d = data ?? [];
      setRows(d);
      setAnni([...new Set(d.map(r => getAnno(r.dataintervento)).filter(Boolean))].sort((a,b) => b-a));
      setColate([...new Set(d.map(r => r.colata).filter(Boolean))].sort());
      setCarri([...new Set(d.map(r => r.carropaniera).filter(Boolean))].sort());
      setLineeAll([...new Set(d.map(r => r.linea).filter(Boolean))].sort((a,b) => a.localeCompare(b,undefined,{numeric:true})));
    } catch (err) {
      console.error('GraficiPaniera fetch:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { if (isOpen) fetchData(); }, [isOpen, fetchData]);

  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  // ── Filtraggio ────────────────────────────────────────────────────────────
  const rowsFiltrate = rows.filter(r => {
    if (annoSel   !== 'TUTTO' && getAnno(r.dataintervento) !== Number(annoSel)) return false;
    if (colataSel !== 'TUTTO' && r.colata      !== colataSel) return false;
    if (carroSel  !== 'TUTTO' && r.carropaniera !== carroSel) return false;
    if (lineaSel  !== 'TUTTO' && r.linea        !== lineaSel) return false;
    return true;
  });

  const lineeVis = [...new Set(rowsFiltrate.map(r => r.linea).filter(Boolean))]
    .sort((a,b) => a.localeCompare(b,undefined,{numeric:true}));

  // ── Label contesto filtri (apparirà dentro i grafici) ────────────────────
  const contextLabel = [
    annoSel   !== 'TUTTO' ? annoSel             : null,
    colataSel !== 'TUTTO' ? `${colataSel}` : null,
    carroSel  !== 'TUTTO' ? ` ${carroSel}` : null,
    lineaSel  !== 'TUTTO' ? ` ${lineaSel}` : null,
  ].filter(Boolean).join(' · ') || null;

  // ── DATI ─────────────────────────────────────────────────────────────────

  // 1. Trend mensile — UNSTACKED, linea separata per linea
  const datiTrend = (() => {
    const map = {};
    for (const r of rowsFiltrate) {
      const key = getMese(r.dataintervento);
      if (!key) continue;
      if (!map[key]) map[key] = { _key: key };
      const l = r.linea ?? 'N/D';
      map[key][l] = (map[key][l] ?? 0) + 1;
      map[key]['_tot'] = (map[key]['_tot'] ?? 0) + 1;
    }
    return Object.values(map)
      .sort((a,b) => {
        const [ma,ya] = a._key.split('/');
        const [mb,yb] = b._key.split('/');
        return new Date(ya, ma-1) - new Date(yb, mb-1);
      })
      .map(d => ({ ...d, mese: meseBreve(d._key) }));
  })();

  // 2. Per linea
  const datiLinea = lineeVis.map(l => ({
    linea:        `${l}`,
    _raw:          l,
    sostituzioni:  rowsFiltrate.filter(r => r.linea === l).length,
  })).sort((a,b) => b.sostituzioni - a.sostituzioni);

  // 3. Top 10 cilindri
  const datiCilindri = (() => {
    const map = {};
    for (const r of rowsFiltrate) {
      const c = r.ncilindro ?? 'N/D';
      map[c] = (map[c] ?? 0) + 1;
    }
    return Object.entries(map)
      .map(([c, n]) => ({ cilindro: `${c}`, sostituzioni: n }))
      .sort((a,b) => b.sostituzioni - a.sostituzioni)
      .slice(0,10);
  })();

  // 4. Confronto annuale — grouped bar per linea
  const datiAnno = (() => {
    const anniT = annoSel === 'TUTTO' ? anni : [Number(annoSel)];
    return anniT.map(a => {
      const obj = { anno: String(a) };
      for (const l of lineeAll) {
        obj[l] = rows.filter(r =>
          getAnno(r.dataintervento) === Number(a) &&
          r.linea === l &&
          (colataSel === 'TUTTO' || r.colata === colataSel) &&
          (carroSel  === 'TUTTO' || r.carropaniera === carroSel)
        ).length;
      }
      return obj;
    });
  })();

  // ── KPI ───────────────────────────────────────────────────────────────────
  const totale    = rowsFiltrate.length;
  const cilUnici  = new Set(rowsFiltrate.map(r => r.ncilindro).filter(Boolean)).size;
  const lineaTop  = datiLinea[0];
  const mediaLuna = datiTrend.length ? (totale / datiTrend.length).toFixed(1) : '0';
  const trendYoy  = (() => {
    if (anni.length < 2) return null;
    const c = rows.filter(r => getAnno(r.dataintervento) === anni[0]).length;
    const p = rows.filter(r => getAnno(r.dataintervento) === anni[1]).length;
    if (!p) return null;
    return Math.round(((c-p)/p)*100);
  })();

  const hasFiltri = annoSel!=='TUTTO'||colataSel!=='TUTTO'||carroSel!=='TUTTO'||lineaSel!=='TUTTO';

  // Stile assi comune
  const AXIS_STYLE = { fontSize:9, fontWeight:700, fill:'rgba(128,128,128,0.5)' };
  const GRID_PROPS = { strokeDasharray:'3 3', stroke:'rgba(128,128,128,0.07)', vertical:false };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/85 backdrop-blur-xl animate-in fade-in duration-300"
        onClick={onClose}
      />

      <div className="relative w-full max-w-6xl bg-card border border-border/50 shadow-2xl rounded-[2.5rem] overflow-hidden flex flex-col max-h-[94vh] animate-in zoom-in-95 duration-300">

        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

        {/* ── HEADER ── */}
        <div className="shrink-0 px-7 py-5 border-b border-border/30 bg-card/90 backdrop-blur-sm flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Icon name="BarChart2" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-black uppercase tracking-tighter text-foreground">
                Dashboard Sostituzioni
              </h2>
              <p className="text-[9px] font-black uppercase tracking-[.25em] text-muted-foreground/40 mt-0.5">
                Analisi interventi · Cilindri Paniera
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasFiltri && (
              <button
                onClick={() => { setAnnoSel('TUTTO'); setColataSel('TUTTO'); setCarroSel('TUTTO'); setLineaSel('TUTTO'); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500/80 text-[10px] font-black uppercase tracking-widest hover:bg-red-500/15 transition-all"
              >
                <Icon name="FilterX" size={12} />
                Reset filtri
              </button>
            )}
            <button onClick={fetchData} disabled={isLoading}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-muted/40 border border-border/30 text-muted-foreground hover:text-primary transition-all disabled:opacity-40">
              <Icon name="RefreshCw" size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-2xl bg-muted/50 hover:bg-red-500/10 hover:text-red-500 border border-border/40 text-muted-foreground transition-all group">
              <Icon name="X" size={17} className="group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>
        </div>

        {/* ── FILTRI ── */}
        <div className="shrink-0 px-7 py-4 border-b border-border/15 bg-muted/5 flex flex-col gap-3">
          <div className="flex flex-wrap gap-x-7 gap-y-2.5">
            <FilterGroup label="Anno"  options={['TUTTO',...anni.map(String)]} value={annoSel}   onChange={setAnnoSel}                                           color="#6366f1" />
            <FilterGroup label="Col."  options={['TUTTO',...colate]}            value={colataSel} onChange={(v)=>{setColataSel(v);setCarroSel('TUTTO');}}         color="#f59e0b" />
            <FilterGroup label="Carro" options={['TUTTO',...carri]}             value={carroSel}  onChange={setCarroSel}                                          color="#10b981" />
            <FilterGroup label="Linea" options={['TUTTO',...lineeAll]}          value={lineaSel}  onChange={setLineaSel}                                          color="#8b5cf6" />
          </div>
          {hasFiltri && (
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-pulse" />
              <span className="text-[9px] font-black uppercase tracking-widest text-primary/50">
                Filtro attivo: {contextLabel}
              </span>
            </div>
          )}
        </div>

        {/* ── CORPO ── */}
        <div
          className="flex-1 overflow-y-auto px-7 py-6 flex flex-col gap-6"
          style={{ scrollbarWidth:'thin', scrollbarColor:'rgba(128,128,128,0.15) transparent' }}
        >

          {isLoading ? (
            <div className="flex flex-col gap-4">
              {[260,210,210,250].map((h,i) => (
                <div key={i} className="rounded-3xl bg-muted/20 animate-pulse" style={{height:h}} />
              ))}
            </div>

          ) : rowsFiltrate.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-28 gap-4">
              <div className="w-16 h-16 rounded-full bg-muted/20 flex items-center justify-center">
                <Icon name="BarChart2" size={28} className="text-muted-foreground/15" />
              </div>
              <span className="text-sm font-black uppercase tracking-widest text-muted-foreground/25">
                Nessun dato per i filtri selezionati
              </span>
              {hasFiltri && (
                <button
                  onClick={() => { setAnnoSel('TUTTO'); setColataSel('TUTTO'); setCarroSel('TUTTO'); setLineaSel('TUTTO'); }}
                  className="text-[10px] font-black uppercase tracking-widest text-primary/60 hover:text-primary transition-colors"
                >
                  Azzera filtri
                </button>
              )}
            </div>

          ) : (
            <>
              {/* ── KPI ── */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard label="Tot. INTERVENTI"    value={fmt(totale)}   sub={contextLabel ?? 'storico completo'}          icon="RefreshCw"   color="#6366f1" trend={trendYoy} />
                <KpiCard label="Cilindri Distinti" value={fmt(cilUnici)} sub="cilindri unici smontati"                     icon="Hash"        color="#06b6d4" />
                <KpiCard label="Linea più Attiva"  value={lineaTop ? lineaTop.linea : '—'} sub={lineaTop ? `${fmt(lineaTop.sostituzioni)} interventi` : ''} icon="Zap" color="#f59e0b" />
                <KpiCard label="Media / Mese"      value={mediaLuna}    sub="interventi medi mensili"                     icon="Calendar"    color="#8b5cf6" />
              </div>

              {/* ── TREND MENSILE ── */}
              {datiTrend.length > 0 && (
                <ChartCard
                  title="Andamento mensile"
                  subtitle={`${datiTrend.length} mesi — interventi per linea`}
                  icon="TrendingUp"
                  contextLabel={contextLabel}
                  actions={
                    <ChartToggle value={tipoTrend} onChange={setTipoTrend} options={[
                      { value:'area', icon:'AreaChart',  label:'Area' },
                      { value:'bar',  icon:'BarChart2',  label:'Bar'  },
                      { value:'line', icon:'TrendingUp', label:'Line' },
                    ]} />
                  }
                >
                  <ResponsiveContainer width="100%" height={260}>

                    {/* ── BAR ── */}
                    {tipoTrend === 'bar' ? (
                      <BarChart data={datiTrend} margin={{top:5,right:5,left:-18,bottom:0}} barGap={2} barCategoryGap="30%">
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="mese" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(128,128,128,0.05)' }} />
                        {lineeVis.map(l => (
                          <Bar key={l} dataKey={l} name={`${l}`}
                            fill={lineaColor(l,lineeAll)}
                            radius={[4,4,0,0]}
                            maxBarSize={28}
                          />
                        ))}
                      </BarChart>

                    /* ── LINE ── */
                    ) : tipoTrend === 'line' ? (
                      <LineChart data={datiTrend} margin={{top:5,right:5,left:-18,bottom:0}}>
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="mese" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ stroke:'rgba(128,128,128,0.15)', strokeWidth:1 }} />
                        {lineeVis.map(l => (
                          <Line key={l} type="monotone" dataKey={l} name={`${l}`}
                            stroke={lineaColor(l,lineeAll)}
                            strokeWidth={2.5}
                            dot={{ r:3, fill:lineaColor(l,lineeAll), strokeWidth:0 }}
                            activeDot={{ r:5, strokeWidth:0 }}
                            connectNulls
                          />
                        ))}
                      </LineChart>

                    /* ── AREA (default) — unstacked, fill molto leggero ── */
                    ) : (
                      <AreaChart data={datiTrend} margin={{top:5,right:5,left:-18,bottom:0}}>
                        <defs>
                          {lineeVis.map(l => {
                            const col = lineaColor(l,lineeAll);
                            return (
                              <linearGradient key={l} id={`ag-${l}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%"   stopColor={col} stopOpacity={0.22} />
                                <stop offset="100%" stopColor={col} stopOpacity={0.01} />
                              </linearGradient>
                            );
                          })}
                        </defs>
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="mese" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ stroke:'rgba(128,128,128,0.12)', strokeWidth:1 }} />
                        {/* Disegna prima le aree (fill), poi le linee sopra — ordine importante */}
                        {lineeVis.map(l => (
                          <Area key={l} type="monotone" dataKey={l} name={`${l}`}
                            stroke={lineaColor(l,lineeAll)}
                            strokeWidth={2.5}
                            fill={`url(#ag-${l})`}
                            fillOpacity={1}
                            connectNulls
                            dot={false}
                            activeDot={{ r:4, strokeWidth:0, fill:lineaColor(l,lineeAll) }}
                          />
                        ))}
                      </AreaChart>
                    )}
                  </ResponsiveContainer>

                  {/* Legenda pills con totale per linea */}
                  <LegendaPills linee={lineeVis} allLinee={lineeAll} dati={datiTrend} />
                </ChartCard>
              )}

              {/* ── ROW: Linee + Top Cilindri ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Distribuzione per linea */}
                {datiLinea.length > 0 && (
                  <ChartCard
                    title="Distribuzione per linea"
                    subtitle="Interventi totali per linea"
                    icon="GitBranch"
                    contextLabel={contextLabel}
                    actions={
                      <ChartToggle value={tipoLinea} onChange={setTipoLinea} options={[
                        { value:'bar', icon:'BarChart2', label:'Bar' },
                        { value:'pie', icon:'PieChart',  label:'Pie' },
                      ]} />
                    }
                  >
                    {tipoLinea === 'pie' ? (
                      <>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={datiLinea}
                              dataKey="sostituzioni"
                              nameKey="linea"
                              cx="50%" cy="50%"
                              innerRadius={50}
                              outerRadius={88}
                              paddingAngle={3}
                              strokeWidth={0}
                            >
                              {datiLinea.map((d,i) => (
                                <Cell key={i} fill={lineaColor(d._raw,lineeAll)} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                        {/* Legenda pie */}
                        <div className="flex flex-col gap-1.5 pt-2 border-t border-border/10">
                          {datiLinea.map(d => {
                            const pct = totale > 0 ? ((d.sostituzioni/totale)*100).toFixed(1) : 0;
                            const col = lineaColor(d._raw,lineeAll);
                            return (
                              <div key={d._raw} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full shrink-0" style={{background:col}} />
                                  <span className="text-[10px] font-bold text-foreground/70">{d.linea}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="w-24 h-1.5 rounded-full bg-muted/30 overflow-hidden">
                                    <div className="h-full rounded-full" style={{width:`${pct}%`,background:col}} />
                                  </div>
                                  <span className="text-[10px] font-black tabular-nums" style={{color:col}}>{d.sostituzioni}</span>
                                  <span className="text-[9px] text-muted-foreground/40 font-bold w-8 text-right">{pct}%</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={datiLinea} margin={{top:5,right:5,left:-18,bottom:0}} barCategoryGap="35%">
                            <CartesianGrid {...GRID_PROPS} />
                            <XAxis dataKey="linea" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                            <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(128,128,128,0.05)' }} />
                            <Bar dataKey="sostituzioni" name="Interventi" radius={[8,8,0,0]} maxBarSize={55}>
                              {datiLinea.map((d,i) => (
                                <Cell key={i} fill={lineaColor(d._raw,lineeAll)} />
                              ))}
                              <LabelList dataKey="sostituzioni" position="top"
                                style={{ fontSize:10, fontWeight:900, fill:'rgba(128,128,128,0.6)' }} />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                        <LegendaPills linee={lineeVis} allLinee={lineeAll} />
                      </>
                    )}
                  </ChartCard>
                )}

                {/* Top 10 cilindri */}
                {datiCilindri.length > 0 && (
                  <ChartCard
                    title="Top 10 cilindri"
                    subtitle="più frequentemente sostituiti"
                    icon="Hash"
                    contextLabel={contextLabel}
                  >
                    <ResponsiveContainer width="100%" height={datiCilindri.length * 28 + 20} minHeight={200}>
                      <BarChart
                        data={datiCilindri}
                        layout="vertical"
                        margin={{top:0,right:45,left:0,bottom:0}}
                        barCategoryGap="25%"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.07)" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis
                          type="category"
                          dataKey="cilindro"
                          width={62}
                          tick={{ fontSize:9, fontWeight:700, fill:'rgba(128,128,128,0.55)' }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(128,128,128,0.05)' }} />
                        <Bar dataKey="sostituzioni" name="Interventi" radius={[0,7,7,0]} maxBarSize={16}>
                          {datiCilindri.map((_,i) => (
                            <Cell key={i} fill={`rgba(99,102,241,${0.95 - i*0.08})`} />
                          ))}
                          <LabelList dataKey="sostituzioni" position="right"
                            style={{ fontSize:10, fontWeight:900, fill:'rgba(99,102,241,0.7)' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}
              </div>

              {/* ── CONFRONTO ANNUALE ── */}
              {anni.length > 0 && (
                <ChartCard
                  title="Confronto annuale per linea"
                  subtitle="intervento per anno — barre raggruppate"
                  icon="Calendar"
                  contextLabel={contextLabel}
                  actions={
                    <ChartToggle value={tipoAnno} onChange={setTipoAnno} options={[
                      { value:'bar',  icon:'BarChart2',  label:'Bar'  },
                      { value:'line', icon:'TrendingUp', label:'Line' },
                    ]} />
                  }
                >
                  <ResponsiveContainer width="100%" height={230}>
                    {tipoAnno === 'line' ? (
                      <LineChart data={datiAnno} margin={{top:5,right:5,left:-18,bottom:0}}>
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="anno" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        {lineeAll.map(l => (
                          <Line key={l} type="monotone" dataKey={l} name={`Linea ${l}`}
                            stroke={lineaColor(l,lineeAll)}
                            strokeWidth={2.5}
                            dot={{ r:5, fill:lineaColor(l,lineeAll), strokeWidth:0 }}
                            activeDot={{ r:7, strokeWidth:0 }}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    ) : (
                      <BarChart data={datiAnno} margin={{top:5,right:5,left:-18,bottom:0}} barCategoryGap="30%" barGap={2}>
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="anno" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(128,128,128,0.05)' }} />
                        {lineeAll.map(l => (
                          <Bar key={l} dataKey={l} name={`${l}`}
                            fill={lineaColor(l,lineeAll)}
                            radius={[5,5,0,0]}
                            maxBarSize={35}
                          />
                        ))}
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                  <LegendaPills linee={lineeAll} allLinee={lineeAll} dati={datiAnno} />
                </ChartCard>
              )}

              {/* ── HEATMAP TABELLA ── */}
              <ChartCard title="Riepilogo linea × anno" subtitle="" icon="Table" contextLabel={contextLabel}>
                <div className="overflow-x-auto rounded-2xl border border-border/20">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-muted/30">
                        <th className="px-4 py-3 text-left text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 border-b border-border/20 whitespace-nowrap">Linea</th>
                        {(annoSel==='TUTTO' ? anni : [Number(annoSel)]).map(a => (
                          <th key={a} className="px-4 py-3 text-center text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 border-b border-border/20">{a}</th>
                        ))}
                        <th className="px-4 py-3 text-center text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 border-b border-border/20">Tot.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineeVis.map((l, li) => {
                        const anniTab = annoSel==='TUTTO' ? anni : [Number(annoSel)];
                        const cnts    = anniTab.map(a => rowsFiltrate.filter(r => r.linea===l && getAnno(r.dataintervento)===Number(a)).length);
                        const maxCnt  = Math.max(...cnts, 1);
                        const totL    = rowsFiltrate.filter(r => r.linea===l).length;
                        const col     = lineaColor(l, lineeAll);
                        return (
                          <tr key={l} className={`border-b border-border/10 last:border-0 ${li%2===0?'':'bg-muted/[0.03]'}`}>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full shrink-0" style={{background:col}} />
                                <span className="text-[10px] font-black uppercase tracking-widest whitespace-nowrap" style={{color:col}}>
                                  {l}
                                </span>
                              </div>
                            </td>
                            {cnts.map((cnt, ci) => {
                              const intensity = cnt / maxCnt;
                              const hex = Math.round(intensity * 55 + 12).toString(16).padStart(2,'0');
                              return (
                                <td key={ci} className="px-4 py-2.5 text-center">
                                  {cnt > 0 ? (
                                    <span className="inline-block px-2.5 py-1 rounded-lg text-[10px] font-black tabular-nums transition-all"
                                      style={{ background:`${col}${hex}`, color:col }}>
                                      {cnt}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-muted-foreground/15 font-bold">—</span>
                                  )}
                                </td>
                              );
                            })}
                            <td className="px-4 py-2.5 text-center">
                              <span className="inline-block px-3 py-1 rounded-lg text-[10px] font-black tabular-nums"
                                style={{ background:`${col}22`, color:col }}>
                                {totL}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {/* Riga totali */}
                      <tr className="bg-muted/15 border-t-2 border-border/20">
                        <td className="px-4 py-2.5 text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">Totale</td>
                        {(annoSel==='TUTTO' ? anni : [Number(annoSel)]).map(a => {
                          const cnt = rowsFiltrate.filter(r => getAnno(r.dataintervento)===Number(a)).length;
                          return (
                            <td key={a} className="px-4 py-2.5 text-center text-[10px] font-black text-foreground/50 tabular-nums">
                              {cnt||'—'}
                            </td>
                          );
                        })}
                        <td className="px-4 py-2.5 text-center">
                          <span className="text-[12px] font-black text-primary tabular-nums">{totale}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </ChartCard>

            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GraficiPaniera;